// src/App.js
import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { SignUp, SignIn, useAuth } from "@clerk/clerk-react";

import LandingPage from "./pages/LandingPage";
import Onboarding from "./pages/Onboarding";
import Courses from "./pages/Courses";
import CourseDetails from "./pages/CourseDetails";
import LessonViewer from "./pages/LessonViewer";
import TestPage from "./pages/TestPage";
import Feedback from "./pages/Feedback";
import Profile from "./pages/Profile";
import DebugHierarchy from "./pages/DebugHierarchy";
import RoleRedirect from "./pages/RoleRedirect";

import LearnerDashboard from "./Dashboard/LearnerDashboard";
import AdminDashboard from "./Dashboard/AdminDashboard";
import InstructorDashboard from "./Dashboard/InstructorDashboard";

import OfflineIndicator from "./components/OfflineIndicator";
import AdminLifecyclePage from "./pages/AdminLifecyclePage";

import { clerkAppearance } from "./pages/clerk_appearance";

const basePath = process.env.PUBLIC_URL || "";

const clerkLoginPath = `${basePath}/login`;
const clerkRegisterPath = `${basePath}/register`;
const clerkSignInAlias = `${basePath}/sign-in`;
const clerkSignUpAlias = `${basePath}/sign-up`;

const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env?.VITE_API_BASE?.replace(/\/+$/, "")) ||
  (process.env.REACT_APP_API_BASE &&
    process.env.REACT_APP_API_BASE.replace(/\/+$/, "")) ||
  "http://localhost:5050";

async function fetchJSON(path, token, { method = "GET", body, signal } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });

  const isJson = res.headers.get("content-type")?.includes("application/json");
  const data = isJson ? await res.json().catch(() => ({})) : {};

  if (!res.ok || data?.ok === false) {
    const msg = data?.error || data?.message || `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.payload = data;
    throw err;
  }

  return data?.data ?? data;
}

function LoadingSpinner({ label = "Loading…" }) {
  return (
    <div className="flex items-center justify-center min-h-screen bg-white">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-3" />
        <div className="text-gray-700 font-semibold">{label}</div>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { isSignedIn, isLoaded, getToken } = useAuth();
  const location = useLocation();

  const [gate, setGate] = useState({
    loading: true,
    redirectTo: null,
    error: "",
  });

  useEffect(() => {
    let cancelled = false;
    const ac = new AbortController();

    (async () => {
      try {
        if (!isLoaded) return;

        if (!isSignedIn) {
          if (!cancelled) {
            setGate({ loading: false, redirectTo: "/login", error: "" });
          }
          return;
        }

        const path = location.pathname;

        const allowNoGate = new Set([
          "/",
          "/role-redirect",
          "/login",
          "/register",
          "/sign-in",
          "/sign-up",
        ]);

        const isAuthPath =
          path === "/login" ||
          path.startsWith("/login/") ||
          path === "/register" ||
          path.startsWith("/register/") ||
          path === "/sign-in" ||
          path.startsWith("/sign-in/") ||
          path === "/sign-up" ||
          path.startsWith("/sign-up/");

        if (allowNoGate.has(path) || isAuthPath) {
          if (!cancelled) {
            setGate({ loading: false, redirectTo: null, error: "" });
          }
          return;
        }

        const token = await getToken().catch(() => null);
        if (!token) {
          if (!cancelled) {
            setGate({ loading: false, redirectTo: "/login", error: "" });
          }
          return;
        }

        // DB is the source of truth for BOTH role and onboarding
        const me = await fetchJSON("/api/users/me", token, { signal: ac.signal });
        console.log("[ProtectedRoute] /api/users/me =", me);

        const role = String(me?.role || "").trim().toLowerCase();
        const onboardingComplete =
          me?.onboarding_complete === true ||
          me?.onboardingComplete === true;

        if (!role) {
          console.error("[ProtectedRoute] missing role from /api/users/me:", me);
          if (!cancelled) {
            setGate({
              loading: false,
              redirectTo: null,
              error: "Could not determine your role.",
            });
          }
          return;
        }

        const isLearner = role === "learner";
        const isOnboardingPath = path === "/onboarding";

        // Only learners should ever be sent to onboarding
        if (isLearner && onboardingComplete !== true && !isOnboardingPath) {
          if (!cancelled) {
            setGate({ loading: false, redirectTo: "/onboarding", error: "" });
          }
          return;
        }

        // Non-learners should never stay on onboarding
        if (!isLearner && isOnboardingPath) {
          if (!cancelled) {
            if (role === "instructor") {
              setGate({
                loading: false,
                redirectTo: "/dashboard/instructor",
                error: "",
              });
            } else {
              setGate({
                loading: false,
                redirectTo: "/dashboard/admin",
                error: "",
              });
            }
          }
          return;
        }

        if (!cancelled) {
          setGate({ loading: false, redirectTo: null, error: "" });
        }
      } catch (e) {
        console.error("[ProtectedRoute] gate error:", e);
        if (!cancelled) {
          setGate({ loading: false, redirectTo: null, error: "" });
        }
      }
    })();

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [isLoaded, isSignedIn, getToken, location.pathname]);

  if (!isLoaded || gate.loading) {
    return <LoadingSpinner label="Checking your session…" />;
  }

  if (gate.redirectTo) {
    return <Navigate to={gate.redirectTo} replace />;
  }

  return (
    <>
      <OfflineIndicator />
      {children}
    </>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />

      <Route
        path="/register/*"
        element={
          <div className="min-h-screen absolute inset-0 bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 flex items-center justify-center">
            <SignUp
              routing="path"
              path={clerkRegisterPath}
              signInUrl={clerkLoginPath}
              afterSignUpUrl={`${basePath}/role-redirect`}
              appearance={clerkAppearance}
            />
          </div>
        }
      />

      <Route
        path="/sign-up/*"
        element={
          <div className="min-h-screen absolute inset-0 bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 flex items-center justify-center">
            <SignUp
              routing="path"
              path={clerkSignUpAlias}
              signInUrl={clerkLoginPath}
              afterSignUpUrl={`${basePath}/role-redirect`}
              appearance={clerkAppearance}
            />
          </div>
        }
      />

      <Route
        path="/login/*"
        element={
          <div className="min-h-screen absolute inset-0 bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 flex items-center justify-center">
            <SignIn
              routing="path"
              path={clerkLoginPath}
              signUpUrl={clerkRegisterPath}
              afterSignInUrl={`${basePath}/role-redirect`}
              appearance={clerkAppearance}
            />
          </div>
        }
      />

      <Route
        path="/sign-in/*"
        element={
          <div className="min-h-screen absolute inset-0 bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 flex items-center justify-center">
            <SignIn
              routing="path"
              path={clerkSignInAlias}
              signUpUrl={clerkRegisterPath}
              afterSignInUrl={`${basePath}/role-redirect`}
              appearance={clerkAppearance}
            />
          </div>
        }
      />

      <Route path="/role-redirect" element={<RoleRedirect />} />

      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        }
      />

      <Route
        path="/courses"
        element={
          <ProtectedRoute>
            <Courses />
          </ProtectedRoute>
        }
      />

      <Route
        path="/courses/:courseId"
        element={
          <ProtectedRoute>
            <CourseDetails />
          </ProtectedRoute>
        }
      />

      <Route
        path="/courses/:courseId/lessons/:lessonId"
        element={
          <ProtectedRoute>
            <LessonViewer />
          </ProtectedRoute>
        }
      />

      <Route
        path="/courses/:courseId/lessons/:lessonId/test"
        element={
          <ProtectedRoute>
            <TestPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />

      <Route
        path="/feedback"
        element={
          <ProtectedRoute>
            <Feedback />
          </ProtectedRoute>
        }
      />

      <Route
        path="/debug-hierarchy"
        element={
          <ProtectedRoute>
            <DebugHierarchy />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/lifecycle"
        element={
          <ProtectedRoute>
            <AdminLifecyclePage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/learner"
        element={
          <ProtectedRoute>
            <LearnerDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/admin"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard/instructor"
        element={
          <ProtectedRoute>
            <InstructorDashboard />
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}